/**
 * Every external system this account has granted access to, in one place.
 *
 * A grant is per account, not per collection, so revoking one here affects
 * every collection reading through it. That is the point of having this page at
 * all: the collection settings can only speak for the collection you are
 * looking at, and a user who wants to know what Eventera can reach needs one
 * list, not a tour of their sidebar.
 */

import { useState } from "react";
import { Unplug } from "lucide-react";

import { useDeleteConnectionMutation, useGetConnectionsQuery } from "@/api/api";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { HGConnection } from "@/common/types";
import { forgetToken } from "@/providers/external/tokens";
import { providerLibrary } from "@/providers/external/registry";
import { Button } from "../button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../sheet";

function systemTitle(systemId: string) {
  // The systems the app knows are named by the providers built on them; an
  // unknown one still deserves a row, under whatever the server called it.
  const provider = providerLibrary.find((p) => p.systemId === systemId);
  return provider
    ? provider.systemId.replace(/^./, (c) => c.toUpperCase())
    : systemId;
}

function ConnectionRow({ connection }: { connection: HGConnection }) {
  const [deleteConnection] = useDeleteConnectionMutation();
  const [busy, setBusy] = useState(false);

  const provider = providerLibrary.find(
    (p) => p.systemId === connection.system_id,
  );
  const Icon = provider?.Icon;

  const onDisconnect = async () => {
    setBusy(true);
    forgetToken(connection.id);
    await deleteConnection(connection.id);
    setBusy(false);
  };

  return (
    <div className="flex items-start gap-2 rounded-[10px] border border-border bg-secondary px-4 py-3">
      {Icon ? <Icon className="mt-0.5 h-4 w-4 shrink-0" /> : null}
      <div className="min-w-0 grow">
        <p className="truncate text-[13.5px] font-semibold">
          {systemTitle(connection.system_id)}
        </p>
        <p className="truncate text-[12px] text-muted-foreground">
          {connection.account_label ?? "Connected account"}
        </p>
        {connection.status !== "active" && (
          <p className="text-[12px] text-destructive">
            Access expired — reconnect from the collection.
          </p>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onDisconnect}
        disabled={busy}
      >
        <Unplug className="h-4 w-4" />
        Disconnect
      </Button>
    </div>
  );
}

export default function ConnectionsSheet({
  children,
  open,
  onOpenChange,
}: {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const me = useAppSelector(selectMe);
  // Only fetched while the sheet is open: most sessions never open it, and a
  // grant list is not something any other surface needs.
  const { data: connections } = useGetConnectionsQuery(undefined, {
    skip: !open,
  });

  if (!me) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}

      <SheetContent
        className="max-sm:inset-0 max-sm:w-full max-sm:border-l-0 sm:w-[380px] sm:max-w-[380px] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="px-5 pt-[18px] pb-3 border-b">
          <SheetTitle
            className="text-[18px] font-semibold text-foreground"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Connected accounts
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-3 px-5 pt-4 pb-5">
          {connections && connections.length > 0 ? (
            <>
              {connections.map((connection) => (
                <ConnectionRow key={connection.id} connection={connection} />
              ))}
              <p className="text-[12px] text-muted-foreground">
                Records from these accounts are fetched in your browser and
                never stored on the server. Disconnecting withdraws access
                immediately, and you can also revoke it from the provider.
              </p>
            </>
          ) : (
            <p className="text-[13px] text-muted-foreground">
              No external accounts connected. Create a collection that mirrors a
              system to connect one.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
